import type { Dispatch, SetStateAction } from 'react'
import {
  Alert,
  Button,
  Card,
  Group,
  NativeSelect,
  ScrollArea,
  Stack,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core'

import { AppModal, InlineLoader } from '@/components/ui'
import {
  AI_INSTRUCTION_PRIORITY_OPTIONS,
  type AiInstructionFormState,
} from '@/components/tickets/ticket-detail-page-helpers'
import type { AIInstructionSimilarRow } from '@/types/api'

type TicketAiInstructionModalProps = {
  opened: boolean
  loading: boolean
  saving: boolean
  error: string | null
  meta: string
  form: AiInstructionFormState
  similar: AIInstructionSimilarRow | null
  similarReason: string
  setForm: Dispatch<SetStateAction<AiInstructionFormState>>
  onClose: () => void
  onSave: (mode: 'create' | 'replace') => void
}

export function TicketAiInstructionModal({
  opened,
  loading,
  saving,
  error,
  meta,
  form,
  similar,
  similarReason,
  setForm,
  onClose,
  onSave,
}: TicketAiInstructionModalProps) {
  return (
    <AppModal
      opened={opened}
      onClose={() => {
        if (!saving) onClose()
      }}
      title="Создать инструкцию по диалогу"
      size="xl"
      centered
      footer={
        <>
          <Button variant="default" disabled={saving} onClick={onClose}>
            Закрыть
          </Button>
          {similar?.id ? (
            <Button
              variant="light"
              color="gray"
              loading={saving}
              disabled={loading}
              onClick={() => onSave('replace')}
            >
              Заменить похожую
            </Button>
          ) : null}
          <Button
            loading={saving}
            disabled={loading || Boolean(error)}
            onClick={() => onSave('create')}
          >
            Добавить инструкцию
          </Button>
        </>
      }
    >
      {loading ? (
        <InlineLoader label="Анализируем диалог, ищем похожие инструкции и формируем черновик…" />
      ) : null}
      {error ? (
        <Alert color="red" title="Ошибка" mb="md">
          {error}
        </Alert>
      ) : null}
      {!loading && !error ? (
        <ScrollArea.Autosize mah="70vh" type="auto">
          <Stack gap="sm">
            {meta ? (
              <Text size="xs" c="dimmed">
                {meta}
              </Text>
            ) : null}
            {similar?.id ? (
              <Card withBorder padding="sm" radius="md" bg="var(--mantine-color-body)">
                <Group justify="space-between" align="flex-start" wrap="nowrap" gap="xs" mb={6}>
                  <Text size="sm" fw={600}>
                    Найдена похожая инструкция
                  </Text>
                  {similarReason ? (
                    <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>
                      {similarReason}
                    </Text>
                  ) : null}
                </Group>
                <Text size="xs" c="dimmed" mb={6}>
                  ID: {similar.id}
                </Text>
                <Text size="xs" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.45 }}>
                  {`Категория: ${similar.category || ''}
Триггеры: ${similar.triggers || ''}
Действие: ${similar.action || ''}
Ответ: ${similar.response_from_db || ''}
Собираемые данные: ${similar.data_collected || ''}
Вопросы: ${similar.questions_asked || ''}
Теги: ${similar.tags || ''}
Приоритет: ${similar.priority || ''}
Когда transfer: ${similar.when_transfer_to_operator || ''}
Сообщение при transfer: ${similar.message_to_client_on_transfer || ''}`}
                </Text>
              </Card>
            ) : null}
            <Group grow align="flex-start">
              <TextInput
                label="Категория"
                size="xs"
                value={form.category}
                onChange={(e) => setForm((f) => ({ ...f, category: e.currentTarget.value }))}
              />
              <NativeSelect
                label="Приоритет"
                size="xs"
                value={form.priority}
                onChange={(e) => setForm((f) => ({ ...f, priority: e.currentTarget.value }))}
                data={[...AI_INSTRUCTION_PRIORITY_OPTIONS]}
              />
            </Group>
            <Textarea
              label="Триггеры"
              size="xs"
              minRows={3}
              value={form.triggers}
              onChange={(e) => setForm((f) => ({ ...f, triggers: e.currentTarget.value }))}
            />
            <Textarea
              label="Действие"
              size="xs"
              minRows={4}
              value={form.action}
              onChange={(e) => setForm((f) => ({ ...f, action: e.currentTarget.value }))}
            />
            <Textarea
              label="Ответ из базы"
              size="xs"
              minRows={3}
              value={form.response_from_db}
              onChange={(e) => setForm((f) => ({ ...f, response_from_db: e.currentTarget.value }))}
            />
            <Group grow align="flex-start">
              <Textarea
                label="Собираемые данные"
                size="xs"
                minRows={3}
                value={form.data_collected}
                onChange={(e) => setForm((f) => ({ ...f, data_collected: e.currentTarget.value }))}
              />
              <Textarea
                label="Задаваемые вопросы"
                size="xs"
                minRows={3}
                value={form.questions_asked}
                onChange={(e) => setForm((f) => ({ ...f, questions_asked: e.currentTarget.value }))}
              />
            </Group>
            <Group grow align="flex-start">
              <TextInput
                label="Теги"
                size="xs"
                value={form.tags}
                onChange={(e) => setForm((f) => ({ ...f, tags: e.currentTarget.value }))}
              />
              <TextInput
                label="Когда передавать оператору"
                size="xs"
                value={form.when_transfer_to_operator}
                onChange={(e) =>
                  setForm((f) => ({ ...f, when_transfer_to_operator: e.currentTarget.value }))
                }
              />
            </Group>
            <Textarea
              label="Сообщение клиенту при передаче"
              size="xs"
              minRows={3}
              value={form.message_to_client_on_transfer}
              onChange={(e) =>
                setForm((f) => ({ ...f, message_to_client_on_transfer: e.currentTarget.value }))
              }
            />
            <Text size="xs" c="dimmed">
              После добавления/замены инструкции пересоздайте ассистента, чтобы изменения применились.
            </Text>
          </Stack>
        </ScrollArea.Autosize>
      ) : null}
    </AppModal>
  )
}
